import cx from "classnames";
import { useCallback, useEffect, useRef, useState } from "react";
import { t } from "ttag";

import ButtonGroup from "metabase/common/components/ButtonGroup";
import { useNumberFormatter } from "metabase/common/hooks/use-number-formatter";
import { getRowCountMessage } from "metabase/common/utils/get-row-count-message";
import CS from "metabase/css/core/index.css";
import { copyData } from "metabase/jerry/Common/copy-to-clipboard";
import SubscribeExportSheet from "metabase/jerry/Spreadsheet/SubscribeExportSheet";
import { useSelector } from "metabase/lib/redux";
import QuestionDownloadPopover from "metabase/query_builder/components/QuestionDownloadPopover";
import { ExecutionTime } from "metabase/query_builder/components/view/ExecutionTime";
import { QuestionDisplayToggle } from "metabase/query_builder/components/view/QuestionDisplayToggle/QuestionDisplayToggle";
import ViewButton from "metabase/query_builder/components/view/ViewButton";
import ViewSection from "metabase/query_builder/components/view/ViewSection";
import { getUser } from "metabase/selectors/user";
import { ActionIcon, Box, Group, Icon, Tooltip } from "metabase/ui";
import type Question from "metabase-lib/v1/Question";
import type {
  Dataset,
  RawSeries,
  VisualizationSettings,
} from "metabase-types/api";

import type { AdhocResult } from "../types";

import S from "./ResultFooter.module.css";
import type { ResultSidebarPanel } from "./ResultSidebar";

export type ResultFooterProps = {
  result: AdhocResult;
  /** The question as executed — download acts on this, not on the editor. */
  question: Question;
  rawSeries: RawSeries | null;
  openSidebarPanel: ResultSidebarPanel | null;
  isShowingRawTable: boolean;
  onToggleSidebarPanel: (panel: ResultSidebarPanel) => void;
  onToggleRawTable: (isShowingRawTable: boolean) => void;
};

/**
 * Footer for one result, laid out like the query builder's `ViewFooter`.
 *
 * `ViewFooter` itself reads the question and its results straight from the
 * query builder store, which only ever holds one question, so it can't be
 * reused as-is by a multi-tab workbench. Its leaf components are prop-driven
 * though, so this composes the same ones.
 */
export function ResultFooter({
  result,
  question,
  rawSeries,
  openSidebarPanel,
  isShowingRawTable,
  onToggleSidebarPanel,
  onToggleRawTable,
}: ResultFooterProps) {
  const formatNumber = useNumberFormatter();
  const user = useSelector(getUser);
  const dataset = result.dataset;
  const hasData = dataset?.data != null && dataset.error == null;

  return (
    <ViewSection
      py="sm"
      className={cx(CS.textMedium, CS.borderTop, CS.fullWidth, S.footer)}
      data-testid="adhoc-result-footer"
    >
      <Group justify="space-between" pos="relative" wrap="nowrap" w="100%">
        {hasData ? (
          <Group className={cx(CS.flex1, S.left)}>
            <ButtonGroup className={S.buttonGroup}>
              <ViewButton
                medium
                labelBreakpoint="sm"
                data-testid="viz-type-button"
                className={S.button}
                active={openSidebarPanel === "chart-type"}
                onClick={() => onToggleSidebarPanel("chart-type")}
              >
                {t`Visualization`}
              </ViewButton>
              <ViewButton
                medium
                onlyIcon
                icon="gear"
                iconSize={16}
                labelBreakpoint="sm"
                data-testid="viz-settings-button"
                aria-label={t`Visualization settings`}
                className={S.button}
                active={openSidebarPanel === "chart-settings"}
                onClick={() => onToggleSidebarPanel("chart-settings")}
              />
            </ButtonGroup>
          </Group>
        ) : (
          <Box flex={1} className={S.left} />
        )}

        {hasData && (
          <QuestionDisplayToggle
            className={CS.mx1}
            isShowingRawTable={isShowingRawTable}
            onToggleRawTable={onToggleRawTable}
          />
        )}

        <Group wrap="nowrap" justify="right" className={S.right}>
          {hasData && <span>{getRowCountMessage(dataset, formatNumber)}</span>}
          {result.status === "cancelled" && <span>{t`Cancelled`}</span>}
          <ExecutionTime time={dataset?.running_time} />
          {hasData && (
            <Group gap="sm" wrap="nowrap">
              <CopyDataButton
                result={dataset}
                rawSeries={rawSeries}
                visualizationSettings={result.visualizationSettings}
                isShowingRawTable={isShowingRawTable}
              />
              <QuestionDownloadPopover question={question} result={dataset} />
              {SubscribeExportSheet.shouldRender({ user, result: dataset }) && (
                <SubscribeExportSheet
                  user={user}
                  result={dataset}
                  question={question}
                  isShowingRawTable={isShowingRawTable}
                  rawSeries={rawSeries}
                  visualizationSettings={result.visualizationSettings}
                />
              )}
            </Group>
          )}
        </Group>
      </Group>
    </ViewSection>
  );
}

const COPIED_FEEDBACK_MS = 2000;

type CopyDataButtonProps = {
  result: Dataset;
  rawSeries: RawSeries | null;
  visualizationSettings: VisualizationSettings;
  isShowingRawTable: boolean;
};

/**
 * Copy the result to the clipboard as tab-separated text, ready to paste into
 * a spreadsheet.
 *
 * The fork's `metabase/jerry/CopyData` reads all of this off the query builder
 * store, which a multi-tab workbench can't populate, so this passes the same
 * values in as props and shares the underlying `copyData` helper.
 */
function CopyDataButton({
  result,
  rawSeries,
  visualizationSettings,
  isShowingRawTable,
}: CopyDataButtonProps) {
  const [isCopied, setIsCopied] = useState(false);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const handleCopy = useCallback(async () => {
    const text = copyData(
      isShowingRawTable,
      result,
      visualizationSettings,
      rawSeries,
    );
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(
        () => setIsCopied(false),
        COPIED_FEEDBACK_MS,
      );
    } catch (error) {
      console.error("Could not copy the result to the clipboard", error);
    }
  }, [isShowingRawTable, rawSeries, result, visualizationSettings]);

  const label = isCopied ? t`Copied!` : t`Copy data to clipboard`;

  return (
    <Tooltip label={label}>
      <ActionIcon
        data-testid="copy-data-button"
        aria-label={label}
        onClick={handleCopy}
      >
        <Icon name={isCopied ? "check" : "copy"} />
      </ActionIcon>
    </Tooltip>
  );
}
